import React, { useState, useEffect } from "react";
import { AdminService } from "../utils";
import { useAsync } from "../../../utils/hooks/useAsync";
import "./index.scss";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Modal from "../../common/Modal/Modal";
import useModal from "../../../utils/hooks/useModal";
import { SingleValue } from "react-select";
import ConfigSelect from "./ConfigSelect";
import Label from "../../Label";
import useDevice from "../../../utils/hooks/useDevice";
import classNames from "classnames";
import { ProductServices } from "../../../services/products";
import Tags from "./Tags";

type Props = {
  setState: React.Dispatch<React.SetStateAction<any>>;
  productEdit: Product;
  setEditVariation?: React.Dispatch<React.SetStateAction<any>>;
  existVariations?: boolean;
};

const AddProduct: React.FC<Props> = ({
  setState,
  productEdit,
  setEditVariation,
  existVariations,
}) => {
  const navigate = useNavigate();
  const { isOpen, toggle } = useModal();
  const { run } = useAsync();
  const buttonCaption = productEdit?._id
    ? "Guardar modificación"
    : "Crear producto";
  const [validaForm, setValidaForm] = useState<{
    title: boolean;
    sku: boolean;
    material: boolean;
    department: boolean;
    category: boolean;
    url: boolean;
    description: boolean;
    form: boolean;
  }>({
    title: false,
    sku: true,
    material: false,
    department: false,
    category: false,
    url: true,
    description: true,
    form: false,
  });
  const [subCategory, setSubCategory] = useState<string>();
  const [showDescription, setShowDescription] = useState<boolean>(false);
  const [product, setProduct] = useState<Product>(productEdit);
  const [categories, setCategories] = useState<CategorySelect[]>([]);
  const [tags, setTags] = useState<Set<string>>(
    new Set(productEdit?.tag?.split(" ").filter((e) => !!e) || [])
  );

  const { isMobile } = useDevice();

  useEffect(() => {
    if (productEdit?._id) {
      setProduct(productEdit);
    }
  }, [productEdit]);

  useEffect(() => {
    validateForm();
  }, [categories]);

  useEffect(() => {
    if (categories.length == 0) {
      run(
        ProductServices.getCategoriesbyDepartment(product.department).then(
          (r) => {
            setCategories(r.data);
          }
        )
      );
    }
    validateForm();
  }, [product]);

  useEffect(() => {
    subCategory &&
      setProduct({
        ...product,
        category: subCategory,
      });
  }, [subCategory]);

  useEffect(() => {
    run(
      ProductServices.getCategoriesbyDepartment(product.department).then(
        (r) => {
          setCategories(r.data);
        }
      )
    );
  }, [product.department]);

  let selectOptions = {
    material: {
      gold: "Oro",
      silver: "Plata",
      importacion: "Importación",
    } as Record<string, string>,
    department: {
      chain: "Cadena",
      brazalete: "Pulsera",
      pendant: "Dije",
      ring: "Anillo",
      importacion: "Importación",
      hoop: "Aro",
      finding: "Fornitura",
    } as Record<string, string>,
  };

  const submitProd = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    prodRegister();
  };

  const prodRegister = async () => {
    product.tag = [...tags].join(" ");
    if (productEdit?._id) {
      if (existVariations && productEdit.material != product.material) {
        toast.warning(
          "El producto tiene variaciones. Elimine las variaciones y vuelva a intentarlo."
        );
      } else {
        run(AdminService.updateProduct(product)).then((r: any) => {
          toast.success("Producto modificado correctamente");
          setTimeout(() => {
            resetForm();
            setState({
              _id: "",
              title: "", // Forzet Larga Oro
              sku: "", // FLORO2023
              material: "",
              department: "", // Chains
              category: "", // Chains/Gold
              friendlyUrl: "", // www.rtabk.com/productos/forzet-largo-oro
              description: "", // El forzet largo es un modelo clasico...
              thumbnailUrl: "", // www.rtabak.com/images/....
              isCustom: false,
              tag: "",
            });
          }, 2000);
        });
      }
    } else {
      run(AdminService.saveProduct(product)).then((r: any) => {
        toast.success("Producto agregado correctamente");
        setTimeout(() => {
          setState(r.data);
          resetForm();
        }, 2000);
      });
    }
  };

  const handleChange = (e: any) => {
    if (e.currentTarget.name == "isCustom") {
      setProduct({
        ...product,
        isCustom: e.currentTarget.checked ? true : false,
      });
    } else {
      setProduct({
        ...product,
        [e.currentTarget.name]: e.currentTarget.value,
      });
    }
  };

  const handleHighlightedChange = (e: any) => {
    if (e.currentTarget.checked) {
      if (e.currentTarget.name == "highlighted") {
        tags.add("HIGHLIGHTED");
      } else if (e.currentTarget.name == "highlightedCategory") {
        tags.add(product.department.toUpperCase());
      }
    } else {
      if (e.currentTarget.name == "highlighted") {
        tags.delete("HIGHLIGHTED");
      } else if (e.currentTarget.name == "highlightedCategory") {
        tags.delete(product.department.toUpperCase());
      }
    }
    setTags(new Set(tags));
  };

  const handleRemoveTag = (tag: string) => {
    tags.delete(tag);
    setTags(new Set(tags));
  };

  const handleSelect = (
    selectedOption: SingleValue<{ value: string; label: string }>,
    e: any
  ) => {
    setSubCategory(
      (e.name === "material" ? selectedOption?.value : product.material) +
        "|" +
        (e.name === "department" ? selectedOption?.value : product.department) +
        "|" +
        (e.name === "category" ? selectedOption?.value : "")
    );
    setProduct({
      ...product,
      [e.name]: selectedOption?.value,
    });
  };

  const resetForm = () => {
    setProduct({
      _id: "",
      title: "", // Forzet Larga Oro
      sku: "", // FLORO2023
      material: "",
      department: "", // Chains
      category: "", // Chains/Gold
      friendlyUrl: "", // www.rtabk.com/productos/forzet-largo-oro
      description: "", // El forzet largo es un modelo clasico...
      thumbnailUrl: "", // www.rtabak.com/images/....
      isCustom: false,
    });
  };

  const handleDelete = () => {
    run(
      AdminService.deleteProduct(productEdit).then((r: any) => {
        toast.success("Producto eliminado correctamente");
        setTimeout(() => {
          setState(r.data);
          resetForm();
          setState({
            _id: "",
            title: "", // Forzet Larga Oro
            sku: "", // FLORO2023
            material: "",
            department: "", // Chains
            category: "", // Chains/Gold
            friendlyUrl: "", // www.rtabk.com/productos/forzet-largo-oro
            description: "", // El forzet largo es un modelo clasico...
            thumbnailUrl: "", // www.rtabak.com/images/....
            isCustom: false,
            tag: "",
          });
          navigate("/admin/add");
        }, 2000);
      })
    );
  };

  const validateForm = () => {
    let auxItem: {
      title: boolean;
      sku: boolean;
      material: boolean;
      department: boolean;
      category: boolean;
      url: boolean;
      description: boolean;
      form: boolean;
    } = {
      title: false,
      sku: true,
      material: false,
      department: false,
      category: false,
      url: true,
      description: true,
      form: false,
    };
    product.title.length > 1 ? (auxItem.title = true) : (auxItem.title = false);
    product.department.length > 3
      ? (auxItem.department = true)
      : (auxItem.department = false);
    product.material.length > 3
      ? (auxItem.material = true)
      : (auxItem.material = false);
    product.category.length > 12
      ? (auxItem.category = true)
      : (auxItem.category = false);
    auxItem.department &&
    auxItem.category &&
    auxItem.description &&
    auxItem.material &&
    auxItem.sku &&
    auxItem.title &&
    auxItem.url
      ? (auxItem.form = true)
      : (auxItem.form = false);
    setValidaForm(auxItem);
    let category =
      (product.category?.includes("|")
        ? categories &&
          categories.find((el) => el.value == product.category?.split("|")[2])
            ?.value
        : categories &&
          categories.find((cate) => cate.value == product.category)?.value) ||
      "";
    setShowDescription(
      ProductServices.showDescription(category, categories) || false
    );
  };

  const childrenEdit = () => (
    <div className="childrenDeleteProduct">
      <div className="childrenDeleteProduct_head">
        <h2>Editar</h2>
        <h1 onClick={toggle}>X</h1>
      </div>
      <h4>¿Estás seguro de borrar este producto?</h4>
      <h3>{productEdit.title}</h3>
      <div className="childrenDeleteProduct_buttonsContainer">
        <button
          type="button"
          className="cancel_button"
          onClick={() => toggle()}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="confirm_button"
          onClick={() => handleDelete()}
        >
          Eliminar
        </button>
      </div>
    </div>
  );

  return (
    <>
      <ToastContainer autoClose={1500} position={"bottom-left"} />
      <div className="addProductContainer">
        <div className="addProductTitle">
          <Label tag="h3" style={{ fontFamily: "Mont-Semibold" }}>
            {productEdit?._id ? product.title : "Nuevo Producto"}
          </Label>
        </div>

        <form action="" id="form" className="productForm" onSubmit={submitProd}>
          <div className="productForm__section">
            <div className="productForm__inputContainer">
              <Label
                tag="label"
                htmlFor="title"
                className="label__grey"
                style={{ fontSize: "14px" }}
              >
                Título
              </Label>
              <input
                type="text"
                className=""
                id="title"
                name="title"
                value={product.title}
                placeholder="Ingrese el titulo"
                onChange={handleChange}
                autoFocus={true}
              />
            </div>
            <ConfigSelect
              productConfig="material"
              handleSelect={handleSelect}
              label="Material"
              options={[
                { value: "gold", label: "Oro" },
                { value: "silver", label: "Plata" },
              ]}
              placeholder="Ingrese el material"
              value={{
                value: product.material,
                label:
                  selectOptions.material[product.material] ||
                  "Seleccionar material",
              }}
            />
          </div>
          <div className="productForm__section">
            <ConfigSelect
              productConfig="department"
              handleSelect={handleSelect}
              label="Categoría"
              options={[
                { value: "chain", label: "Cadena" },
                { value: "brazalete", label: "Pulsera" },
                { value: "pendant", label: "Dije" },
                { value: "ring", label: "Anillo" },
                { value: "hoop", label: "Aro" },
                { value: "finding", label: "Fornitura" },
              ]}
              placeholder="Ingrese la categoría"
              value={{
                value: product.department,
                label:
                  selectOptions.department[product.department] ||
                  "Seleccionar categoría",
              }}
            />
            {categories && (
              <ConfigSelect
                productConfig="category"
                handleSelect={handleSelect}
                label="Subcategoría"
                options={categories}
                placeholder="Ingrese subcategoría"
                value={{
                  value: product.category?.includes("|")
                    ? product.category?.split("|")[2]
                    : product.category,
                  label:
                    (product.category?.includes("|")
                      ? categories.find(
                          (cate) =>
                            cate.value == product.category?.split("|")[2]
                        )?.label
                      : categories.find(
                          (cate) => cate.value == product.category
                        )?.label) || "Seleccionar subcategoría",
                }}
              />
            )}
          </div>
          <div className="productForm__section">
            <div className="productForm__inputContainer">
              <Label
                tag="label"
                htmlFor="sku"
                className="label__grey"
                style={{ fontSize: "14px" }}
              >
                Código
              </Label>
              <input
                type="text"
                id="sku"
                value={product.sku}
                name="sku"
                style={!validaForm.sku ? { color: "red" } : {}}
                className=""
                placeholder="Ingrese la sku"
                onChange={handleChange}
              ></input>
            </div>
            {showDescription ? (
              <div className="productForm__inputContainer">
                <Label
                  tag="label"
                  htmlFor="description"
                  className="label__grey"
                >
                  Descripción
                </Label>
                <textarea
                  className=""
                  id="description"
                  name="description"
                  value={product.description}
                  placeholder="Ingrese la descripcion"
                  onChange={handleChange}
                ></textarea>
              </div>
            ) : (
              <div className="productForm__inputContainer">
                <Label
                  style={{ color: "transparent" }}
                  tag="label"
                  htmlFor="description"
                  className="label__grey"
                >
                  Descripción
                </Label>
                <textarea
                  disabled
                  style={{ backgroundColor: "transparent" }}
                ></textarea>
              </div>
            )}
            <div className="productForm__section">
              <div className="productForm__inputContainer">
                <Label
                  tag="label"
                  htmlFor="title"
                  className="label__grey"
                  style={{ fontSize: "14px" }}
                >
                  Customizable
                </Label>
                <div className="productForm__isCustom">
                  <input
                    type="checkbox"
                    id="isCustom"
                    name="isCustom"
                    style={{ backgroundColor: "red", color: "red" }}
                    checked={product.isCustom}
                    onChange={handleChange}
                    onClick={handleChange}
                  ></input>
                  <Label
                    tag="label"
                    htmlFor="isCustom"
                    className="productForm__isCustom__label"
                  >
                    Customizable
                  </Label>
                </div>
              </div>
            </div>
          </div>
          <div className="highlighted">
            <div>
              <Label
                tag="label"
                htmlFor="title"
                className="label__grey"
                style={{ fontSize: "14px" }}
              >
                Carrusel
              </Label>
            </div>
            <div className="highlighted_checks">
              <div className="highlighted_check">
                <input
                  type="checkbox"
                  id="highlighted"
                  name="highlighted"
                  style={{ backgroundColor: "red", color: "red" }}
                  checked={tags.has("HIGHLIGHTED")}
                  onChange={handleHighlightedChange}
                  onClick={handleHighlightedChange}
                ></input>
                <Label tag="label" htmlFor="highlighted">
                  Destacados
                </Label>
              </div>
              <div className="highlighted_check">
                <input
                  type="checkbox"
                  id="highlightedCategory"
                  name="highlightedCategory"
                  checked={
                    product.department
                      ? tags.has(product.department.toUpperCase())
                      : false
                  }
                  onChange={handleHighlightedChange}
                  onClick={handleHighlightedChange}
                ></input>
                <Label tag="label" htmlFor="highlightedCategory">
                  Su categoría
                </Label>
              </div>
            </div>
            <div>
              <Tags
                tags={[...tags]?.filter(
                  (tag) =>
                    tag !== "HIGHLIGHTED" &&
                    tag !== product.department.toUpperCase()
                )}
                addTag={(newTag: string) => setTags(new Set([...tags, newTag]))}
                removeTag={(tag: string) => handleRemoveTag(tag)}
              />
            </div>
          </div>
        </form>

        <div className="buttonsContainer">
          {validaForm.form ? (
            <button
              onClick={submitProd}
              type="submit"
              className={classNames("buttonsContainer__button", {
                "buttonsContainer__button-mobile": isMobile,
              })}
            >
              {buttonCaption}
            </button>
          ) : (
            <button
              type="submit"
              disabled={true}
              className={classNames("buttonsContainer__button", {
                "buttonsContainer__button-mobile": isMobile,
              })}
            >
              {buttonCaption}
            </button>
          )}
          {productEdit?._id && setEditVariation && (
            <>
              <button
                type="button"
                className={classNames("buttonsContainer_delete", {
                  "buttonsContainer_delete--mobile": isMobile,
                })}
                onClick={toggle}
              >
                Eliminar producto
              </button>
              {isOpen && (
                <Modal
                  isOpen={true}
                  toggle={toggle}
                  children={childrenEdit()}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AddProduct;
